import React from 'react'
import { Close } from '@mui/icons-material'
import { Button, createTheme, CssBaseline, Dialog, DialogActions, DialogProps, Grid, IconButton, ThemeProvider, Typography, useTheme } from '@mui/material'

import { generateTheme } from '../../providers/mui-theme-provider'

import classes from './basic-dialog.styles'

export interface IBasicDialogProps extends DialogProps {
  title?: string
  children?: React.ReactNode
  textContent?: string | React.ReactNode | React.ReactNode[] | React.JSX.Element | (() => React.JSX.Element)
  onClose: () => void
  closeLabel?: string
  hideCancelButton?: boolean
  onConfirm?: () => void
  confirmLabel?: string
  confirmButton?: React.ReactNode
  confirmLoading?: boolean
  icon?: React.ReactNode
  showCloseIcon?: boolean
  disableBackdropClick?: boolean
}

const BasicDialog = (props: IBasicDialogProps) => {
  const {
    onClose,
    closeLabel,
    hideCancelButton = false,
    onConfirm,
    confirmLabel,
    confirmLoading,
    confirmButton,
    title,
    icon,
    children,
    textContent,
    showCloseIcon,
    disableBackdropClick = false,
    maxWidth = 'xs',
    ...rest
  } = props

  const theme = useTheme()

  return (
    // Force light theme for dialog to ensure it looks correct when used in dark mode screens
    <ThemeProvider theme={createTheme(generateTheme('light'))}>
      <CssBaseline />
      <Dialog
        onClose={onClose}
        slotProps={{
          paper: {
            sx: classes.dialogPaper,
          },
        }}
        slots={{
          backdrop: (backdropProps) => (
            <div
              {...backdropProps}
              onClick={() => {
                if (!disableBackdropClick) {
                  props.onClose?.()
                }
              }}
              style={{
                ...backdropProps.style,
                ...classes.blurSkrim(theme),
              }}
            />
          ),
        }}
        maxWidth={maxWidth}
        {...rest}
      >
        {showCloseIcon || title ? (
          <Grid container={true} size={12} sx={classes.headerContainer(theme, !!showCloseIcon)}>
            {showCloseIcon && (
              <Grid>
                <IconButton onClick={onClose} data-testid="close-modal-btn" aria-label="Close dialog" sx={classes.closeButton}>
                  <Close />
                </IconButton>
              </Grid>
            )}
            {title && (
              <Grid sx={classes.titleContainer}>
                <Typography variant="h6" color="textPrimary" sx={classes.titleText(!!showCloseIcon)}>
                  {title}
                </Typography>
              </Grid>
            )}
          </Grid>
        ) : null}

        <Grid container={true} sx={classes.contentContainer}>
          {icon}
          {typeof textContent === 'string' ? (
            <Typography variant="body1" color="textPrimary">
              {textContent}
            </Typography>
          ) : typeof textContent === 'function' ? (
            textContent()
          ) : (
            <Grid size={12}>{textContent}</Grid>
          )}
          {children && !textContent && children}
        </Grid>

        {!hideCancelButton || confirmLabel || confirmButton ? (
          <DialogActions sx={{ ...classes.dialogActions(theme, !hideCancelButton && (!!confirmLabel || !!confirmButton)) }}>
            {!hideCancelButton && <Button onClick={onClose}>{closeLabel || 'Cancel'}</Button>}
            {confirmLabel && !confirmButton && (
              <Button variant="contained" disableElevation onClick={onConfirm} disabled={confirmLoading} loading={confirmLoading}>
                {confirmLabel}
              </Button>
            )}
            {!confirmLabel && confirmButton && confirmButton}
          </DialogActions>
        ) : null}
      </Dialog>
    </ThemeProvider>
  )
}

export default BasicDialog
